import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

interface User {
  _id: string;
  username: string;
  email: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showChangeRole, setShowChangeRole] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.get(`${config.API_BASE_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: { username: string; email: string; password: string; role: string }) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      await axios.post(`${config.API_BASE_URL}/auth/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowCreateUser(false);
      setError('');
      await loadUsers(); // Reload users list
      alert('User created successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const deleteUser = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      await axios.delete(`${config.API_BASE_URL}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setError('');
      await loadUsers(); // Reload users list
      alert(`User "${username}" deleted successfully!`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const resetPassword = async (userId: string, newPassword: string) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      await axios.put(`${config.API_BASE_URL}/auth/users/${userId}/reset-password`, {
        newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowResetPassword(false);
      setSelectedUser(null);
      setError('');
      alert('Password reset successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setShowResetPassword(true);
  };

  const handleChangeRole = (user: User) => {
    setSelectedUser(user);
    setShowChangeRole(true);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-danger';
      case 'supervisor':
        return 'bg-warning';
      case 'tech':
        return 'bg-info';
      case 'manager':
        return 'bg-success';
      case 'staff':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  };

  const changeRole = async (userId: string, newRole: string) => {
    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      await axios.put(`${config.API_BASE_URL}/auth/users/${userId}/change-role`, {
        newRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowChangeRole(false);
      setSelectedUser(null);
      setError('');
      await loadUsers(); // Reload users list
      alert('User role changed successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change user role');
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show m-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
          ></button>
        </div>
      )}

      {/* Header with Create User Button */}
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="bi bi-people me-2"></i>
            Manage Users ({users.length})
          </h6>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreateUser(true)}
          >
            <i className="bi bi-person-plus me-1"></i>
            Create User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-responsive">
        <table className="table table-striped table-hover mb-0">
          <thead className="table-dark">
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  <i className="bi bi-people fs-1 text-muted"></i>
                  <p className="text-muted mt-2">No users found</p>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <strong>{user.username}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <small className="text-muted">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </small>
                  </td>
                  <td>
                    <small className="text-muted">
                      {new Date(user.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </small>
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        className="btn btn-outline-info"
                        onClick={() => handleChangeRole(user)}
                        title="Change role"
                      >
                        <i className="bi bi-person-gear"></i>
                      </button>
                      <button
                        className="btn btn-outline-warning"
                        onClick={() => handleResetPassword(user)}
                        title="Reset password"
                      >
                        <i className="bi bi-key"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => deleteUser(user._id, user.username)}
                        title={user.role === 'admin' && users.filter(u => u.role === 'admin').length <= 1 ? 'Cannot delete the last admin user' : 'Delete user'}
                        disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length <= 1} // Prevent deleting the last admin
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-plus me-2"></i>
                  Create New User
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateUser(false)}></button>
              </div>
              <CreateUserForm onSubmit={createUser} onCancel={() => setShowCreateUser(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && selectedUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-key me-2"></i>
                  Reset Password for {selectedUser.username}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowResetPassword(false)}></button>
              </div>
              <ResetPasswordForm 
                onSubmit={(newPassword) => resetPassword(selectedUser._id, newPassword)} 
                onCancel={() => setShowResetPassword(false)} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showChangeRole && selectedUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-person-gear me-2"></i>
                  Change Role for {selectedUser.username}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowChangeRole(false)}></button>
              </div>
              <ChangeRoleForm 
                currentRole={selectedUser.role}
                users={users}
                onSubmit={(newRole) => changeRole(selectedUser._id, newRole)} 
                onCancel={() => setShowChangeRole(false)} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create User Form Component
interface CreateUserFormProps {
  onSubmit: (userData: { username: string; email: string; password: string; role: string }) => void;
  onCancel: () => void;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'staff'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.username && formData.email && formData.password) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        <div className="mb-3">
          <label htmlFor="username" className="form-label">Username</label>
          <input
            type="text"
            className="form-control"
            id="username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
            minLength={3}
            maxLength={30}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="password" className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            id="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            minLength={6}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="role" className="form-label">Role</label>
          <select
            className="form-select"
            id="role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="tech">Tech</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          <i className="bi bi-person-plus me-1"></i>
          Create User
        </button>
      </div>
    </form>
  );
};

// Reset Password Form Component
interface ResetPasswordFormProps {
  onSubmit: (newPassword: string) => void;
  onCancel: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (formData.newPassword) {
      onSubmit(formData.newPassword);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        <div className="mb-3">
          <label htmlFor="newPassword" className="form-label">New Password</label>
          <input
            type="password"
            className="form-control"
            id="newPassword"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            required
            minLength={6}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
          <input
            type="password"
            className="form-control"
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            required
            minLength={6}
          />
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-warning">
          <i className="bi bi-key me-1"></i>
          Reset Password
        </button>
      </div>
    </form>
  );
};

// Change Role Form Component
interface ChangeRoleFormProps {
  currentRole: string;
  users: User[];
  onSubmit: (newRole: string) => void;
  onCancel: () => void;
}

const ChangeRoleForm: React.FC<ChangeRoleFormProps> = ({ currentRole, users, onSubmit, onCancel }) => {
  const [selectedRole, setSelectedRole] = useState(currentRole);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRole && selectedRole !== currentRole) {
      onSubmit(selectedRole);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="modal-body">
        <div className="mb-3">
          <label htmlFor="currentRole" className="form-label">Current Role</label>
          <input
            type="text"
            className="form-control"
            id="currentRole"
            value={currentRole}
            disabled
          />
        </div>
        <div className="mb-3">
          <label htmlFor="newRole" className="form-label">New Role</label>
          <select
            className="form-select"
            id="newRole"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            required
          >
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="tech">Tech</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-info"
          disabled={selectedRole === currentRole || (currentRole === 'admin' && users.filter(u => u.role === 'admin').length <= 1 && selectedRole !== 'admin')}
          title={currentRole === 'admin' && users.filter(u => u.role === 'admin').length <= 1 && selectedRole !== 'admin' ? 'Cannot change the last admin user to a different role' : 'Change user role'}
        >
          <i className="bi bi-person-gear me-1"></i>
          Change Role
        </button>
      </div>
    </form>
  );
};

export default UserManagement;
